import { useAtom } from 'jotai'
import { MouseEvent as ReactMouseEvent, useState } from 'react'
import { useEffectEvent } from '../../../utilities/use-effect-event'
import { transformerReducer } from './reducer'
import { transformerActionStateAtom, transformerPreviewStateAtom, transformerStateAtom } from './state'
import { useTransformerStore } from './transformer-store'

export function TransformerGizmoRotate({ transformedRef }: { transformedRef: React.RefObject<HTMLDivElement | null> }) {
	const store = useTransformerStore()
	const [actionState, setActionState] = useAtom(transformerActionStateAtom, { store })
	const [previewState, setPreviewState] = useAtom(transformerPreviewStateAtom, { store })
	const [mainState, setMainState] = useAtom(transformerStateAtom, { store })
	const [lastEventCoordinates, setLastEventCoordinates] = useState<{ x: number; y: number } | null>(null)

	const dragMoveHandler = useEffectEvent((event: MouseEvent) => {
		event.preventDefault()
		event.stopPropagation()
		setLastEventCoordinates({ x: event.pageX, y: event.pageY })
		setPreviewState(() => {
			return transformerReducer(mainState, {
				type: 'handleRotationChange',
				actionState: actionState,
				dragCoordinates: { x: event.pageX, y: event.pageY },
				snap: !event.ctrlKey,
			})
		})
	})

	const keyDownHandler = useEffectEvent((event: KeyboardEvent) => {
		if (event.key === 'Control' && lastEventCoordinates) {
			setPreviewState(() => {
				return transformerReducer(mainState, {
					type: 'handleRotationChange',
					actionState: actionState,
					dragCoordinates: lastEventCoordinates,
					snap: false,
				})
			})
		}
	})

	const keyUpHandler = useEffectEvent((event: KeyboardEvent) => {
		if (event.key === 'Control' && lastEventCoordinates) {
			setPreviewState(() => {
				return transformerReducer(mainState, {
					type: 'handleRotationChange',
					actionState: actionState,
					dragCoordinates: lastEventCoordinates,
					snap: true,
				})
			})
		}
	})

	const dragEndHandler = useEffectEvent(() => {
		setActionState(null)
		if (previewState) {
			setMainState(previewState)
			setPreviewState(null)
		}

		window.removeEventListener('mousemove', dragMoveHandler)
		window.removeEventListener('mouseup', dragEndHandler)
		window.removeEventListener('keydown', keyDownHandler)
		window.removeEventListener('keyup', keyUpHandler)
	})

	const handleDragStart = useEffectEvent((event: ReactMouseEvent<HTMLDivElement>) => {
		event.preventDefault()
		event.stopPropagation()

		setActionState({
			type: 'rotation',
			start: { x: event.pageX, y: event.pageY },
			center: {
				x: (transformedRef.current?.getBoundingClientRect().x ?? 0)
					+ (transformedRef.current?.getBoundingClientRect().width ?? 0) / 2,
				y: (transformedRef.current?.getBoundingClientRect().y ?? 0)
					+ (transformedRef.current?.getBoundingClientRect().height ?? 0) / 2,
			},
			startRotation: mainState.rotation,
		})

		window.addEventListener('mousemove', dragMoveHandler)
		window.addEventListener('mouseup', dragEndHandler)
		window.addEventListener('keydown', keyDownHandler)
		window.addEventListener('keyup', keyUpHandler)
	})
	return (
		<div
			className="size-4 cursor-grab"
			onMouseDown={handleDragStart}
		>
			<div className="size-2 relative left-1 top-1 bg-[yellow] ring ring-yellow-500" />
		</div>
	)
}
